import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { TerminalSelect, TerminalLabel, TerminalAlert, TerminalLoading } from './shared/TerminalForms';


const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
`;

const Title = styled(TerminalTitle)`
  margin-bottom: 2rem;
  font-size: 2rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventsCard = styled(TerminalCard)`
  margin-top: 2rem;
`;

const EventsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const EventsTitle = styled.h2`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '[ ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' ]';
    color: ${theme.colors.primary};
  }
`;

const EventsInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text};
`;

const EventGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const EventCard = styled(TerminalCard)`
  transition: all ${theme.transitions.normal};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glowStrong};
  }
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const EventTitle = styled.h3`
  font-family: ${theme.fonts.mono};
  font-size: 1.1rem;
  font-weight: 700;
  color: ${theme.colors.text};
  margin: 0;
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const EventCountry = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text};
  text-transform: uppercase;
`;

const EventImportance = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.importance?.toLowerCase()) {
      case 'high': return 'rgba(255, 0, 64, 0.2)';
      case 'medium': return 'rgba(255, 170, 0, 0.2)';
      case 'low': return 'rgba(0, 255, 65, 0.2)';
      default: return theme.colors.bgSecondary;
    }
  }};
  border: 1px solid ${props => {
    switch (props.importance?.toLowerCase()) {
      case 'high': return theme.colors.error;
      case 'medium': return theme.colors.warning;
      case 'low': return theme.colors.success;
      default: return theme.colors.border;
    }
  }};
  color: ${props => {
    switch (props.importance?.toLowerCase()) {
      case 'high': return theme.colors.error;
      case 'medium': return theme.colors.warning;
      case 'low': return theme.colors.success;
      default: return theme.colors.text;
    }
  }};
`;

const EventDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  font-weight: 600;
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  color: ${theme.colors.text};
  font-weight: 500;
`;

const EventTime = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-top: 0.5rem;
  
  &::before {
    content: '[TIME] ';
    color: ${theme.colors.textMuted};
    font-weight: 600;
  }
`;

const SourceLink = styled.a`
  display: inline-block;
  margin-top: 0.75rem;
  color: ${theme.colors.primary};
  text-decoration: none;
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 500;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    color: ${theme.colors.primaryLight};
    text-decoration: underline;
  }
  
  &::before {
    content: '[SOURCE] ';
    color: ${theme.colors.textMuted};
  }
`;

const NoEvents = styled.div`
  text-align: center;
  padding: 3rem;
  font-family: ${theme.fonts.mono};
  color: ${theme.colors.textMuted};
  
  &::before {
    content: '[NO EVENTS] ';
    color: ${theme.colors.primary};
    font-weight: 700;
  }
`;

const EconomicEvents = () => {
  const [weekRange, setWeekRange] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterImportance, setFilterImportance] = useState('all');
  const [filterType, setFilterType] = useState('');

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/economic-events',
        {
          params: {
            w: weekRange
          }
        }
      );

      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred while fetching events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [weekRange]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEvents = data?.data?.filter(event => {
    const matchesCountry = !filterCountry || event.country === filterCountry;
    const matchesImportance = filterImportance === 'all' || event.importance === filterImportance;
    const matchesType = !filterType || event.type?.toLowerCase().includes(filterType.toLowerCase());
    return matchesCountry && matchesImportance && matchesType;
  }) || [];

  const uniqueCountries = [...new Set(data?.data?.map(event => event.country) || [])].sort();
  const uniqueTypes = [...new Set(data?.data?.map(event => event.type) || [])].sort();

  return (
    <Container>
      <Title>Economic Calendar Events</Title>

      <TerminalCard>
        <FormGrid>
          <FormGroup>
            <TerminalLabel>Week Range</TerminalLabel>
            <TerminalSelect
              value={weekRange}
              onChange={(e) => setWeekRange(parseInt(e.target.value))}
            >
              <option value={1}>This Week</option>
              <option value={2}>Next Week</option>
              <option value={3}>Week 3</option>
              <option value={4}>Week 4</option>
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Filter by Country</TerminalLabel>
            <TerminalSelect
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
            >
              <option value="">All Countries</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Filter by Importance</TerminalLabel>
            <TerminalSelect
              value={filterImportance}
              onChange={(e) => setFilterImportance(e.target.value)}
            >
              <option value="all">All Importance Levels</option>
              <option value="high">High Importance</option>
              <option value="medium">Medium Importance</option>
              <option value="low">Low Importance</option>
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Filter by Type</TerminalLabel>
            <TerminalSelect
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </TerminalSelect>
          </FormGroup>
        </FormGrid>
      </TerminalCard>

      {error && (
        <TerminalAlert className="error" style={{ marginTop: '1rem' }}>
          {error}
        </TerminalAlert>
      )}

      {loading && (
        <TerminalLoading>
          Loading economic events...
        </TerminalLoading>
      )}

      {data && (
        <EventsCard>
          <EventsHeader>
            <EventsTitle>Economic Events Calendar</EventsTitle>
            <EventsInfo>
              <StatItem>
                Range: {data.range || 'N/A'}
              </StatItem>
              <StatItem>
                Total: {data.total_count || 0}
              </StatItem>
              <StatItem>
                Filtered: {filteredEvents.length}
              </StatItem>
            </EventsInfo>
          </EventsHeader>

          {filteredEvents.length === 0 ? (
            <NoEvents>
              No events found. Try adjusting your filters or selecting a different week range.
            </NoEvents>
          ) : (
            <EventGrid>
              {filteredEvents.map((event, index) => (
                <EventCard key={index}>
                  <EventHeader>
                    <EventTitle>{event.title}</EventTitle>
                    <EventMeta>
                      <EventCountry>
                        {event.country}
                      </EventCountry>
                      <EventImportance importance={event.importance}>
                        {event.importance?.toUpperCase() || 'UNKNOWN'}
                      </EventImportance>
                    </EventMeta>
                  </EventHeader>

                  <EventDetails>
                    <DetailItem>
                      <DetailLabel>Type</DetailLabel>
                      <DetailValue>{event.type}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Currency</DetailLabel>
                      <DetailValue>{event.currency}</DetailValue>
                    </DetailItem>
                    {event.previous && (
                      <DetailItem>
                        <DetailLabel>Previous</DetailLabel>
                        <DetailValue>
                          {event.previous}{event.unit ? ` ${event.unit}` : ''}
                        </DetailValue>
                      </DetailItem>
                    )}
                    {event.forecast && (
                      <DetailItem>
                        <DetailLabel>Forecast</DetailLabel>
                        <DetailValue>
                          {event.forecast}{event.unit ? ` ${event.unit}` : ''}
                        </DetailValue>
                      </DetailItem>
                    )}
                    {event.reference_date && (
                      <DetailItem>
                        <DetailLabel>Reference Date</DetailLabel>
                        <DetailValue>
                          {new Date(event.reference_date).toLocaleDateString()}
                        </DetailValue>
                      </DetailItem>
                    )}
                  </EventDetails>

                  <EventTime>
                    {formatDate(event.date)}
                  </EventTime>

                  {event.source_url && (
                    <SourceLink 
                      href={event.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      View Source
                    </SourceLink>
                  )}
                </EventCard>
              ))}
            </EventGrid>
          )}
        </EventsCard>
      )}
    </Container>
  );
};

export default EconomicEvents;
